import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../api';

// Superadmin-only. Controls the daily go-live reward: how much Gold a host who
// stays live 58 minutes gets (once per BDT day), and whether it runs at all.
// The server's sweeper reads this live, so a change takes effect within a minute
// — no deploy.
const RewardSettings = () => {
  const [enabled, setEnabled] = useState(true);
  const [gold, setGold] = useState(5000);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let alive = true;
    apiGet('/api/admin/reward-settings')
      .then((r) => { if (alive) { setEnabled(r.goLiveRewardEnabled !== false); setGold(r.goLiveRewardGold ?? 5000); } })
      .catch((e) => { if (alive) setMsg({ err: true, text: e.message }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const g = Number(gold);
      if (Number.isNaN(g) || g < 0) throw new Error('Reward Gold must be a non-negative number.');
      const r = await apiPost('/api/admin/reward-settings', { goLiveRewardEnabled: enabled, goLiveRewardGold: Math.floor(g) });
      if (r.success === false) throw new Error(r.message);
      setEnabled(r.goLiveRewardEnabled !== false);
      setGold(r.goLiveRewardGold ?? g);
      setMsg({ err: false, text: 'Saved. Takes effect within a minute.' });
    } catch (e) {
      setMsg({ err: true, text: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 24, color: 'rgba(255,255,255,0.6)' }}>Loading…</div>;

  return (
    <div style={{ padding: 24, maxWidth: 520 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800 }}>🎁 Go-Live Reward</h2>
      <p style={{ margin: '0 0 20px', color: 'rgba(255,255,255,0.55)', fontSize: 14, lineHeight: 1.5 }}>
        A video host who stays live for <b>58 minutes</b> earns this many <b>Gold</b>, once per day.
        It counts toward their salary tier, just like gifted Gold.
      </p>

      <div style={{ background: '#13132a', border: '1px solid rgba(255,77,109,0.2)', borderRadius: 16, padding: 22 }}>
        {/* On / off */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <div>
            <div style={{ fontWeight: 700 }}>Reward enabled</div>
            <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.45)' }}>Turn the daily go-live bonus on or off.</div>
          </div>
          <button
            onClick={() => setEnabled((v) => !v)}
            style={{
              width: 52, height: 30, borderRadius: 15, border: 'none', cursor: 'pointer',
              background: enabled ? '#22c55e' : 'rgba(255,255,255,0.2)',
              position: 'relative', transition: 'background 0.15s',
            }}
            aria-label="toggle reward"
          >
            <span style={{
              position: 'absolute', top: 3, left: enabled ? 25 : 3, width: 24, height: 24,
              borderRadius: 12, background: '#fff', transition: 'left 0.15s',
            }} />
          </button>
        </div>

        {/* Amount */}
        <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>Reward Gold</label>
        <input
          type="number"
          min="0"
          value={gold}
          disabled={!enabled}
          onChange={(e) => setGold(e.target.value)}
          style={{
            width: '100%', padding: '11px 14px', borderRadius: 10, fontSize: 16,
            background: enabled ? '#0c0c1e' : 'rgba(12,12,30,0.5)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.12)', outline: 'none',
          }}
        />
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
          Set to 0 (or turn it off) to pay nothing.
        </div>

        <button
          onClick={save}
          disabled={saving}
          style={{
            marginTop: 22, width: '100%', padding: '12px', borderRadius: 10, border: 'none',
            background: 'linear-gradient(135deg,#ff4d6d,#c9184a)', color: '#fff',
            fontWeight: 800, fontSize: 15, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>

        {msg && (
          <div style={{ marginTop: 14, fontSize: 13.5, color: msg.err ? '#ff6b6b' : '#4ade80' }}>
            {msg.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default RewardSettings;
